/**
 * Stylesheet, script, and component asset loader.
 *
 * Every non-CDN asset the target page needs goes through one of
 * these helpers so the inventory stays in sync and so cross-
 * navigation re-loads are avoided. The three surfaces:
 *
 * - `loadStylesheet(href)` - injects a `<link rel="stylesheet">`,
 *   resolves when the sheet has either loaded or errored.
 * - `loadScript(ref)` - injects a `<script src>`, stamping the
 *   declared `type` on the created element: Vite-built chunks are
 *   ES modules, and a bare tag evaluates them as classic scripts
 *   ("Cannot use import statement outside a module"), leaving the
 *   arriving components server-rendered but inert. Resolves on
 *   both load and error. Errors are non-fatal (and deliberately
 *   resolved rather than rejected) because re-evaluating an
 *   already-loaded Drupal behavior bundle can throw `const
 *   already declared` errors which must not block the page.
 * - `preloadComponents(ids, registry)` - dynamic-imports the SDX
 *   component bundles for the target page. URL resolution is
 *   delegated to the framework-agnostic ComponentResolverRegistry:
 *   sdx_router has no built-in knowledge of React, Vue, or
 *   Svelte, so each framework module registers its own resolver
 *   from its mount script. On pages where no resolver is
 *   registered, preloading is a graceful no-op - the mount
 *   scripts still import their own component bundles lazily.
 *
 * `loadAssets(plan)` composes all three for a parsed page: new
 * stylesheets are loaded concurrently, new scripts sequentially
 * (to preserve dependency order), and components concurrently.
 */

import { logger } from './logger';
import type { AssetInventory } from './asset-inventory';
import type { ComponentResolverRegistry } from './component-resolver';

/**
 * A script the target page declares: its URL plus the script type
 * when one must survive to the injected tag (ES modules).
 */
export interface ScriptRef {
  src: string;
  type?: string;
}

export interface LoadPlan {
  newStyles: string[];
  newScripts: ScriptRef[];
  componentIds?: string[];
}

export function loadAssets(
  plan: LoadPlan,
  inventory: AssetInventory,
  resolvers: ComponentResolverRegistry,
): Promise<unknown> {
  const promises: Promise<unknown>[] = [];

  for (const href of plan.newStyles) {
    promises.push(loadStylesheet(href, inventory));
  }

  // Scripts load sequentially: most Drupal core behavior bundles
  // depend on their predecessors being evaluated first, and
  // parallelizing would risk racing an `extend Base` definition
  // against its `class Base {}` declaration.
  if (plan.newScripts.length > 0) {
    let scriptChain: Promise<unknown> = Promise.resolve();
    for (const ref of plan.newScripts) {
      scriptChain = scriptChain.then(() => loadScript(ref, inventory));
    }
    promises.push(scriptChain);
  }

  if (plan.componentIds && plan.componentIds.length > 0 && resolvers.hasResolvers()) {
    promises.push(preloadComponents(plan.componentIds, resolvers));
  }

  return Promise.all(promises);
}

function loadStylesheet(href: string, inventory: AssetInventory): Promise<void> {
  return new Promise<void>((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
    inventory.markStylesheetLoaded(href);
  });
}

function loadScript(ref: ScriptRef, inventory: AssetInventory): Promise<void> {
  return new Promise<void>((resolve) => {
    const script = document.createElement('script');
    script.src = ref.src;
    if (ref.type) {
      script.type = ref.type;
    }
    script.onload = () => resolve();
    script.onerror = () => {
      logger.warn(`Failed to load script: ${ref.src}`);
      resolve();
    };
    document.head.appendChild(script);
    inventory.markScriptLoaded(ref.src);
  });
}

function preloadComponents(
  componentIds: string[],
  resolvers: ComponentResolverRegistry,
): Promise<unknown> {
  const imports = componentIds.map((id) => {
    const url = resolvers.resolve(id);
    if (!url) return Promise.resolve(null);
    return import(/* @vite-ignore */ url).catch((err: unknown) => {
      const message = err instanceof Error ? err.message : String(err);
      logger.warn(`Failed to preload ${id}: ${message}`);
      return null;
    });
  });

  return Promise.all(imports);
}
